import { db, storage } from '@/data/firebase/config';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { Banner, BannerAttachment } from '@/domain/types/collections';
import { useAdminLogger } from '@/domain/use-cases/admin-logger';
import {
  checkAndDeactivateIfNeeded,
  reorderActiveBanners,
} from '@/domain/use-cases/manage-banners';
import BannerAttachments from '@/presentation/components/banner-attachments';
import BannerImageUpload from '@/presentation/components/banner-image-upload';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { useState } from 'react';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { MdWarning } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const BannerAddPage = () => {
  const navigate = useNavigate();
  const { logCreate } = useAdminLogger();

  const [formData, setFormData] = useState({
    title: '',
    titleEN: '',
    subtitle: '',
    subtitleEN: '',
    description: '',
    descriptionEN: '',
    isActive: true,
  });

  const [bannerImage, setBannerImage] = useState<File | null>(null);
  const [bannerImageEN, setBannerImageEN] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<BannerAttachment[]>([]);
  const [attachmentsEN, setAttachmentsEN] = useState<BannerAttachment[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleActiveToggle = () => {
    setFormData((prev) => ({ ...prev, isActive: !prev.isActive }));
  };

  const handleSave = async () => {
    // Validaciones
    if (!formData.title.trim()) {
      toast.error('El título (ES) es requerido', { theme: 'colored' });
      return;
    }
    if (formData.title.length > 40) {
      toast.error('El título (ES) no puede exceder 40 caracteres', {
        theme: 'colored',
      });
      return;
    }
    if (!formData.titleEN.trim()) {
      toast.error('El título (EN) es requerido', { theme: 'colored' });
      return;
    }
    if (formData.titleEN.length > 40) {
      toast.error('El título (EN) no puede exceder 40 caracteres', {
        theme: 'colored',
      });
      return;
    }
    if (formData.subtitle && formData.subtitle.length > 20) {
      toast.error('El subtítulo (ES) no puede exceder 20 caracteres', {
        theme: 'colored',
      });
      return;
    }
    if (formData.subtitleEN && formData.subtitleEN.length > 20) {
      toast.error('El subtítulo (EN) no puede exceder 20 caracteres', {
        theme: 'colored',
      });
      return;
    }
    if (!formData.description.trim()) {
      toast.error('La descripción (ES) es requerida', { theme: 'colored' });
      return;
    }
    if (formData.description.length > 90) {
      toast.error('La descripción (ES) no puede exceder 90 caracteres', {
        theme: 'colored',
      });
      return;
    }
    if (!formData.descriptionEN.trim()) {
      toast.error('La descripción (EN) es requerida', { theme: 'colored' });
      return;
    }
    if (formData.descriptionEN.length > 90) {
      toast.error('La descripción (EN) no puede exceder 90 caracteres', {
        theme: 'colored',
      });
      return;
    }
    if (!bannerImage) {
      toast.error('La imagen del banner (ES) es requerida', {
        theme: 'colored',
      });
      return;
    }
    if (!bannerImageEN) {
      toast.error('La imagen del banner (EN) es requerida', {
        theme: 'colored',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Si se va a activar este banner, verificar el límite de 3
      if (formData.isActive) {
        const result = await checkAndDeactivateIfNeeded();
        if (result.deactivated) {
          toast.info(
            'Se desactivó automáticamente el banner más antiguo para mantener el límite de 3',
            { theme: 'colored', autoClose: 5000 },
          );
        }
      }

      // Subir imagen del banner (ES)
      toast.info('Subiendo imágenes del banner...');
      const imageFileName = `${Date.now()}_${bannerImage.name}`;
      const imageRef = ref(storage, `banners/images/${imageFileName}`);
      const uploadTask = uploadBytesResumable(imageRef, bannerImage);

      const imageUrl = await new Promise<string>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          null,
          (error) => reject(error),
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          },
        );
      });

      // Subir imagen del banner (EN)
      const imageFileNameEN = `${Date.now()}_en_${bannerImageEN.name}`;
      const imageRefEN = ref(storage, `banners/images/${imageFileNameEN}`);
      const uploadTaskEN = uploadBytesResumable(imageRefEN, bannerImageEN);

      const imageUrlEN = await new Promise<string>((resolve, reject) => {
        uploadTaskEN.on(
          'state_changed',
          null,
          (error) => reject(error),
          async () => {
            const url = await getDownloadURL(uploadTaskEN.snapshot.ref);
            resolve(url);
          },
        );
      });

      // Crear documento del banner
      const bannerData: Omit<Banner, 'id'> = {
        title: formData.title,
        titleEN: formData.titleEN,
        subtitle: formData.subtitle || undefined,
        subtitleEN: formData.subtitleEN || undefined,
        description: formData.description,
        descriptionEN: formData.descriptionEN,
        imageUrl: imageUrl,
        imageUrlEN: imageUrlEN,
        attachments: attachments,
        attachmentsEN: attachmentsEN,
        isActive: formData.isActive,
        order: 0, // Se actualizará después
        createdAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any,
      };

      const docRef = await addDoc(
        collection(db, DOCUMENTS_DB.BANNERS),
        bannerData,
      );

      // Reordenar banners activos
      if (formData.isActive) {
        await reorderActiveBanners();
      }

      // Log the action
      await logCreate(DOCUMENTS_DB.BANNERS, docRef.id, formData.title, {
        ...bannerData,
        imageUrl,
      });

      toast.success('Banner creado correctamente', { theme: 'colored' });
      navigate('/dashboard/banners');
    } catch (error) {
      console.error('Error creating banner:', error);
      toast.error('Error al crear el banner', { theme: 'colored' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const remainingTitleChars = 40 - formData.title.length;
  const remainingTitleENChars = 40 - formData.titleEN.length;
  const remainingSubtitleChars = 20 - formData.subtitle.length;
  const remainingSubtitleENChars = 20 - (formData.subtitleEN?.length ?? 0);
  const remainingDescChars = 90 - formData.description.length;
  const remainingDescENChars = 90 - formData.descriptionEN.length;

  return (
    <div className="p-4 flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard/banners')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold">Crear Banner</h1>
        </div>

        <button
          onClick={handleSave}
          disabled={isSubmitting}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FiSave />
          {isSubmitting ? 'Guardando...' : 'Guardar Banner'}
        </button>
      </div>

      {/* Formulario */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna izquierda - Información básica */}
        <div className="lg:col-span-2 space-y-6">
          {/* Título y descripción */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h2 className="text-xl font-semibold">Información Básica</h2>

            {/* Títulos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    ES
                  </span>
                  Título *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  maxLength={40}
                  placeholder="Título en español (máx. 40 caracteres)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <p
                  className={`text-xs mt-1 ${remainingTitleChars < 10 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingTitleChars} caracteres restantes
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-gray-600 text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    EN
                  </span>
                  Title *
                </label>
                <input
                  type="text"
                  name="titleEN"
                  value={formData.titleEN}
                  onChange={handleInputChange}
                  maxLength={40}
                  placeholder="Title in English (max. 40 chars)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-gray-400 focus:border-gray-500"
                />
                <p
                  className={`text-xs mt-1 ${remainingTitleENChars < 10 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingTitleENChars} characters remaining
                </p>
              </div>
            </div>

            {/* Subtítulos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    ES
                  </span>
                  Subtítulo
                </label>
                <input
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleInputChange}
                  maxLength={20}
                  placeholder="Subtítulo opcional (máx. 20 caracteres)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <p
                  className={`text-xs mt-1 ${remainingSubtitleChars < 5 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingSubtitleChars} caracteres restantes
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-gray-600 text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    EN
                  </span>
                  Subtitle
                </label>
                <input
                  type="text"
                  name="subtitleEN"
                  value={formData.subtitleEN}
                  onChange={handleInputChange}
                  maxLength={20}
                  placeholder="Optional subtitle (max. 20 chars)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-gray-400 focus:border-gray-500"
                />
                <p
                  className={`text-xs mt-1 ${remainingSubtitleENChars < 5 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingSubtitleENChars} characters remaining
                </p>
              </div>
            </div>

            {/* Descripciones */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    ES
                  </span>
                  Descripción breve *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength={90}
                  rows={3}
                  placeholder="Descripción en español (máx. 90 caracteres)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                />
                <p
                  className={`text-xs mt-1 ${remainingDescChars < 20 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingDescChars} caracteres restantes
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-gray-600 text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    EN
                  </span>
                  Brief description *
                </label>
                <textarea
                  name="descriptionEN"
                  value={formData.descriptionEN}
                  onChange={handleInputChange}
                  maxLength={90}
                  rows={3}
                  placeholder="Description in English (max. 90 chars)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-gray-400 focus:border-gray-500 resize-none"
                />
                <p
                  className={`text-xs mt-1 ${remainingDescENChars < 20 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingDescENChars} characters remaining
                </p>
              </div>
            </div>
          </div>

          {/* Imágenes del banner */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">
              Imágenes del Banner *
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <span className="inline-block bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded">
                    ES
                  </span>
                  Imagen en Español *
                </h3>
                <BannerImageUpload
                  onFileChange={setBannerImage}
                  inputId="banner-image-upload-es"
                />
              </div>
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <span className="inline-block bg-gray-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                    EN
                  </span>
                  Image in English *
                </h3>
                <BannerImageUpload
                  onFileChange={setBannerImageEN}
                  inputId="banner-image-upload-en"
                />
              </div>
            </div>
          </div>

          {/* Adjuntos */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Adjuntos Opcionales</h2>
            <p className="text-sm text-gray-600 mb-4">
              Puedes agregar archivos o enlaces relacionados con este banner
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <span className="inline-block bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded">
                    ES
                  </span>
                  Adjunto en Español
                </h3>
                <BannerAttachments
                  attachments={attachments}
                  onChange={setAttachments}
                />
              </div>
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <span className="inline-block bg-gray-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                    EN
                  </span>
                  Attachment in English
                </h3>
                <BannerAttachments
                  attachments={attachmentsEN}
                  onChange={setAttachmentsEN}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Columna derecha - Estado y opciones */}
        <div className="space-y-6">
          {/* Estado de publicación */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Estado</h2>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-800">Publicar banner</p>
                <p className="text-xs text-gray-600">
                  {formData.isActive ? 'Activo' : 'Inactivo'}
                </p>
              </div>
              <button
                onClick={handleActiveToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.isActive ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {formData.isActive && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <MdWarning className="text-yellow-600 text-lg mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Límite de banners activos
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Solo pueden haber 3 banners activos. Si ya existen 3, el
                      más antiguo se desactivará automáticamente.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Info adicional */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">
              Información importante
            </h3>
            <ul className="space-y-1 text-sm text-blue-700">
              <li>• El banner se mostrará en la página principal</li>
              <li>• Los adjuntos son opcionales</li>
              <li>• Puedes editar el banner después de crearlo</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerAddPage;
